/**
 * Progress — XP, Level & lifetime-stat tracking (Arsenal "Progress" tab).
 *
 * Own currency, deliberately decoupled from Fugazi/Tickets (see
 * docs/XP_PROGRESSION_PLAN.md section 5.1). Persisted via StorageAdapter
 * under the 'progress' key, same pattern as inventory.js/wallet.js.
 *
 * This module stays a leaf: it never imports inventory.js/wallet.js/
 * directives.js. Callers (js/main.js, js/engine/directives.js,
 * js/engine/inventory.js, js/engine/wallet.js) push events in; achievement
 * conditions are evaluated against a "facts" object the caller assembles
 * from whichever other state (inventory/wallet/directives) they hold - see
 * js/data/achievements.js.
 */

import { storageAdapter } from './storageAdapter.js';
import { getAvatarCatalog, getAvatarById } from './avatarCatalog.js';

// XP(level -> level+1) = XP_PER_LEVEL_BASE * level (linear per-level
// increment -> quadratic cumulative requirement). See
// docs/XP_PROGRESSION_PLAN.md section 5.3.
export const XP_PER_LEVEL_BASE = 50;

// "Season 1" content boundary - the formula itself is unbounded (a player
// past this keeps leveling, just without new defined perks yet).
export const LEVEL_CONTENT_CAP = 55;

// Two independent, additive XP components per graded match (never
// multiplied together - a loss with a top grade must still net real XP,
// see docs/XP_PROGRESSION_PLAN.md section 5.2).
export const OUTCOME_XP = Object.freeze({ win: 40, loss: 15 });
export const GRADE_XP = Object.freeze({ D: 0, C: 5, B: 15, 'B+': 22, A: 32, 'A+': 45, 'S-': 60, S: 75, 'S+': 100 });

export const ACHIEVEMENT_TIER_XP = Object.freeze({
  bronze: 50,
  silver: 150,
  gold: 300,
  platinum: 1000 // milestone platinums; "Orbital Apex" (Level 55) overrides to 0 as an endpoint - see achievements.js
});

// Reused verbatim from the Gamerank post-match title strings (js/main.js)
// instead of inventing new copy - see docs/XP_PROGRESSION_PLAN.md 5.3.
const LEVEL_TITLE_TIERS = Object.freeze([
  { maxLevel: 11, title: 'FIELD RECON' },
  { maxLevel: 22, title: 'TACTICAL OPERATOR' },
  { maxLevel: 33, title: 'ELITE SPECIALIST' },
  { maxLevel: 44, title: 'MASTER COMBATANT' },
  { maxLevel: Infinity, title: 'ORBITAL APEX' }
]);

// Cumulative XP required to REACH `level` (level 1 = 0 XP).
export function xpForLevel(level) {
  const L = Math.max(1, Math.floor(level));
  return 25 * L * (L - 1);
}

export function levelFromXp(totalXp) {
  const xp = Math.max(0, Math.floor(totalXp) || 0);
  // Closed-form inverse of xpForLevel(), then nudged to the exact
  // boundary - avoids a linear scan for very large XP totals.
  let level = Math.floor((25 + Math.sqrt(625 + 100 * xp)) / 50);
  level = Math.max(1, level);
  while (xpForLevel(level + 1) <= xp) level++;
  while (level > 1 && xpForLevel(level) > xp) level--;
  return level;
}

export function titleForLevel(level) {
  for (const tier of LEVEL_TITLE_TIERS) {
    if (level <= tier.maxLevel) return tier.title;
  }
  return LEVEL_TITLE_TIERS[LEVEL_TITLE_TIERS.length - 1].title;
}

// Full level/XP-bar breakdown for a given lifetime XP total.
export function levelProgress(totalXp) {
  const xp = Math.max(0, Math.floor(totalXp) || 0);
  const level = levelFromXp(xp);
  const floorXp = xpForLevel(level);
  const nextFloorXp = xpForLevel(level + 1);
  const span = nextFloorXp - floorXp;
  return {
    level,
    title: titleForLevel(level),
    totalXp: xp,
    xpIntoLevel: xp - floorXp,
    xpForThisLevel: span,
    xpToNextLevel: nextFloorXp - xp,
    ratio: span > 0 ? (xp - floorXp) / span : 1
  };
}

const RECENT_EVENTS_MAX = 150;

// Older builds logged a separate `xp` event next to every `match` and
// `achievement` event (each already carries its own `xp`). Those are pure
// duplicates and are dropped on load; `directive`/`daily_jackpot` xp events
// have no primary event of their own and stay.
function isLegacyDuplicateXpEvent(ev) {
  if (ev.type !== 'xp' || typeof ev.reason !== 'string') return false;
  return ev.reason === 'match' || ev.reason.startsWith('achievement:');
}

function nowIso() {
  return new Date().toISOString();
}

class ProgressManager {
  constructor() {
    this._listeners = new Set();
    this._state = this._load();
  }

  reload() {
    this._state = this._load();
    this._notify();
  }

  _defaultState() {
    return {
      version: 1,
      xp: { total: 0 },
      lifetimeFugaziEarned: 0,
      uniquePerksCollected: 0,
      combat: {
        matchesByMode: { skirmish: 0, online: 0 },
        wins: 0,
        losses: 0,
        gradeCounts: { D: 0, C: 0, B: 0, 'B+': 0, A: 0, 'A+': 0, 'S-': 0, S: 0, 'S+': 0 },
        compositeSum: 0,
        gradedMatches: 0,
        currentWinStreak: 0,
        bestWinStreak: 0,
        lifetimeDamage: 0
      },
      unitMvpCounts: {},
      targetDroneHits: 0,
      dailyJackpotDates: [],
      achievements: { unlocked: {} },
      equippedAvatarId: null,
      recentEvents: []
    };
  }

  _load() {
    const raw = storageAdapter.get('progress', null);
    if (!raw || typeof raw.xp?.total !== 'number') {
      const initial = this._defaultState();
      storageAdapter.set('progress', initial);
      return initial;
    }
    // Defensive merge so a state saved by an older/partial version of this
    // module (or hand-edited localStorage) never crashes on a missing key.
    const def = this._defaultState();
    const merged = {
      ...def,
      ...raw,
      xp: { ...def.xp, ...raw.xp },
      combat: {
        ...def.combat,
        ...raw.combat,
        matchesByMode: { ...def.combat.matchesByMode, ...raw.combat?.matchesByMode },
        gradeCounts: { ...def.combat.gradeCounts, ...raw.combat?.gradeCounts }
      },
      unitMvpCounts: { ...(raw.unitMvpCounts || {}) },
      dailyJackpotDates: Array.isArray(raw.dailyJackpotDates) ? raw.dailyJackpotDates : [],
      achievements: { unlocked: { ...(raw.achievements?.unlocked || {}) } },
      recentEvents: Array.isArray(raw.recentEvents) ? raw.recentEvents.filter(ev => ev && typeof ev === 'object' && !isLegacyDuplicateXpEvent(ev)) : []
    };
    return merged;
  }

  _save() {
    storageAdapter.set('progress', this._state);
    this._notify();
  }

  _notify(detail = {}) {
    for (const listener of this._listeners) {
      try { listener(this._state, detail); } catch (err) { console.error('[ProgressManager] Listener error:', err); }
    }
  }

  addListener(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  _pushRecentEvent(entry) {
    this._state.recentEvents.push({ at: nowIso(), ...entry });
    if (this._state.recentEvents.length > RECENT_EVENTS_MAX) {
      this._state.recentEvents.splice(0, this._state.recentEvents.length - RECENT_EVENTS_MAX);
    }
  }

  // --- XP & Level ---

  getLevelProgress() {
    return levelProgress(this._state.xp.total);
  }

  /**
   * Adds XP and detects a level-up. Returns the level-up info (or null) so
   * the caller can trigger the level-up overlay/sound - this module has no
   * UI/audio dependency itself.
   */
  addXp(amount, reason = 'xp', { log = true } = {}) {
    const qty = Math.max(0, Math.floor(amount) || 0);
    if (qty === 0) return null;

    const before = levelFromXp(this._state.xp.total);
    this._state.xp.total += qty;
    const after = levelFromXp(this._state.xp.total);
    // `log: false` for callers whose own log event (match/achievement)
    // already carries the xp - avoids a duplicate row in Recent Activity.
    if (log) this._pushRecentEvent({ type: 'xp', amount: qty, reason });
    if (after > before) {
      this._pushRecentEvent({ type: 'levelup', from: before, to: after, title: titleForLevel(after) });
    }
    this._save();

    if (after > before) {
      const info = { oldLevel: before, newLevel: after, title: titleForLevel(after) };
      this._notify({ levelUp: info });
      return info;
    }
    return null;
  }

  // --- Kennzahl C: Fugazi All-Time ---

  recordFugaziEarned(amount) {
    const qty = Math.max(0, Math.floor(amount) || 0);
    if (qty === 0) return;
    this._state.lifetimeFugaziEarned += qty;
    this._save();
  }

  getLifetimeFugaziEarned() {
    return this._state.lifetimeFugaziEarned;
  }

  // --- Kennzahl B: Gesamtzahl einzigartiger Pack-/Slotmachine-Gewinne ---

  recordUniquePerkUnlocked() {
    this._state.uniquePerksCollected += 1;
    this._save();
  }

  getUniquePerksCollected() {
    return this._state.uniquePerksCollected;
  }

  // --- Kennzahl A: Lifetime-Kampfstatistik & Gesamtranking ---

  /**
   * Records one graded match (Skirmish or Online only - Exhibition/Sandbox
   * are never graded, see docs/GAMERANK_PLAN.md). Awards XP from the two
   * independent components (outcome + grade) and returns a receipt the
   * caller can show/log, plus an embedded level-up info if one occurred.
   */
  recordMatchResult({ mode = 'skirmish', isVictory, grade = 'C', composite = 0, damageDealt = 0 } = {}) {
    const c = this._state.combat;
    if (mode === 'online') c.matchesByMode.online += 1;
    else c.matchesByMode.skirmish += 1;

    if (isVictory) {
      c.wins += 1;
      c.currentWinStreak += 1;
      c.bestWinStreak = Math.max(c.bestWinStreak, c.currentWinStreak);
    } else {
      c.losses += 1;
      c.currentWinStreak = 0;
    }

    const cleanGrade = Object.prototype.hasOwnProperty.call(c.gradeCounts, grade) ? grade : 'C';
    c.gradeCounts[cleanGrade] += 1;
    c.compositeSum += Math.max(0, Math.min(100, composite));
    c.gradedMatches += 1;
    c.lifetimeDamage += Math.max(0, Math.floor(damageDealt) || 0);

    // Rank D is an anti-abuse / abort tier and yields 0 XP
    const outcomeXp = cleanGrade === 'D' ? 0 : (isVictory ? OUTCOME_XP.win : OUTCOME_XP.loss);
    const gradeXp = cleanGrade === 'D' ? 0 : (GRADE_XP[cleanGrade] ?? 0);
    const totalXp = outcomeXp + gradeXp;

    this._pushRecentEvent({ type: 'match', mode, isVictory, grade: cleanGrade, xp: totalXp });
    this._save();

    const levelUp = totalXp > 0 ? this.addXp(totalXp, 'match', { log: false }) : { levelUp: false, oldLevel: this.getLevelProgress().level, newLevel: this.getLevelProgress().level, unlockedAvatars: [] };

    return { outcomeXp, gradeXp, totalXp, levelUp };
  }

  getAverageGrade() {
    const c = this._state.combat;
    if (c.gradedMatches === 0) return { average: 0, grade: 'C', gradedMatches: 0 };
    const average = c.compositeSum / c.gradedMatches;
    return { average, gradedMatches: c.gradedMatches };
  }

  getCombatStats() {
    return { ...this._state.combat, gradeCounts: { ...this._state.combat.gradeCounts }, matchesByMode: { ...this._state.combat.matchesByMode } };
  }

  // --- Unit MVP tallies (feeds the "Veteran [Einheit]" achievement) ---

  recordUnitMvp(unitName) {
    if (!unitName) return;
    this._state.unitMvpCounts[unitName] = (this._state.unitMvpCounts[unitName] || 0) + 1;
    this._save();
  }

  getUnitMvpCounts() {
    return { ...this._state.unitMvpCounts };
  }

  getBestUnitMvpCount() {
    const counts = Object.values(this._state.unitMvpCounts);
    return counts.length ? Math.max(...counts) : 0;
  }

  // --- Arsenal target-drone hits (feeds "Zielübung") ---

  recordTargetDroneHit() {
    this._state.targetDroneHits += 1;
    this._save();
  }

  getTargetDroneHits() {
    return this._state.targetDroneHits;
  }

  // --- Daily directive jackpot day-tracking (feeds "Tägliche Disziplin") ---

  recordDailyJackpot(dateKey = new Date().toISOString().slice(0, 10)) {
    if (!this._state.dailyJackpotDates.includes(dateKey)) {
      this._state.dailyJackpotDates.push(dateKey);
      this._save();
    }
  }

  getDailyJackpotDayCount() {
    return this._state.dailyJackpotDates.length;
  }

  // --- Avatare (Kategorie F, Abschnitt 6.1) ---

  getAvatarCatalogWithUnlockState() {
    const level = levelFromXp(this._state.xp.total);
    return getAvatarCatalog().map(a => ({ ...a, unlocked: a.unlockLevel <= level }));
  }

  getEquippedAvatarId() {
    return this._state.equippedAvatarId || null;
  }

  getEquippedAvatar() {
    const id = this.getEquippedAvatarId();
    return id ? getAvatarById(id) : null;
  }

  /** Equips an avatar (or null to revert to the default animated portrait). */
  equipAvatar(avatarId) {
    if (!avatarId) {
      this._state.equippedAvatarId = null;
      this._save();
      return true;
    }
    const level = levelFromXp(this._state.xp.total);
    const entry = getAvatarById(avatarId);
    if (!entry || entry.unlockLevel > level) return false;
    this._state.equippedAvatarId = avatarId;
    this._save();
    return true;
  }

  // --- Achievements ---

  isAchievementUnlocked(id) {
    return !!this._state.achievements.unlocked[id];
  }

  getUnlockedAchievementIds() {
    return Object.keys(this._state.achievements.unlocked);
  }

  /**
   * Evaluates every locked achievement's condition(facts) and unlocks any
   * that now qualify, awarding its XP. `facts` is assembled by the caller
   * from whatever other state (inventory/wallet/this module) is relevant -
   * see js/data/achievements.js for the shape and js/main.js for the
   * assembly points (match end, directive completion, inventory change).
   * Returns the list of newly-unlocked achievement defs (empty if none).
   */
  evaluateAchievements(defs, facts) {
    const newlyUnlocked = [];
    for (const def of defs) {
      if (this.isAchievementUnlocked(def.id)) continue;
      let met = false;
      try {
        met = !!def.condition(facts);
      } catch (err) {
        console.error(`[ProgressManager] Achievement condition threw for '${def.id}':`, err);
      }
      if (!met) continue;

      this._state.achievements.unlocked[def.id] = { at: nowIso() };
      this._pushRecentEvent({ type: 'achievement', id: def.id, xp: def.xp || 0 });
      newlyUnlocked.push(def);
    }
    if (newlyUnlocked.length > 0) {
      this._save();
      for (const def of newlyUnlocked) {
        if (def.xp > 0) this.addXp(def.xp, `achievement:${def.id}`, { log: false });
      }
    }
    return newlyUnlocked;
  }

  getRecentEventCount() {
    return this._state.recentEvents.length;
  }

  getRecentEvents(limit = 10) {
    return this._state.recentEvents.slice(-limit).reverse();
  }

  getState() {
    const combat = this.getCombatStats();
    const totalMatches = ((combat.matchesByMode?.skirmish || 0) + (combat.matchesByMode?.online || 0)) || ((combat.wins || 0) + (combat.losses || 0));
    return {
      xp: { ...this._state.xp },
      lifetimeFugaziEarned: this._state.lifetimeFugaziEarned,
      uniquePerksCollected: this._state.uniquePerksCollected,
      combat,
      stats: {
        matchesPlayed: totalMatches,
        wins: combat.wins || 0,
        losses: combat.losses || 0
      },
      unitMvpCounts: this.getUnitMvpCounts(),
      targetDroneHits: this._state.targetDroneHits,
      dailyJackpotDates: [...this._state.dailyJackpotDates],
      achievements: { unlocked: { ...this._state.achievements.unlocked } },
      unlockedAchievements: Object.keys(this._state.achievements.unlocked),
      recentEventCount: this._state.recentEvents.length,
      equippedAvatarId: this._state.equippedAvatarId || null
    };
  }

  resetProgress() {
    this._state = this._defaultState();
    this._save();
    return this._state;
  }
}

export const progress = new ProgressManager();
export { ProgressManager };
